// Contoso CDX tenant — Guardian demo data.
//
// Every value here derives from src/lib/demo/data/*.json, a sanitized
// export of Microsoft's CDX `Contoso` tenant from iuvo-guardian-db-dev.
// Nothing is hand-fabricated; nothing is real customer data. To refresh,
// drop a new export bundle in data/ and rebuild.

/* eslint-disable @typescript-eslint/no-explicit-any */

import { DEMO_TENANT_ID, DEMO_TENANT_NAME, DEMO_TENANT_DOMAIN } from './flag'
import { dataset, camel, camelDeep } from './dataset'

export const NOW = () => new Date().toISOString()
const daysAgo = (n: number) => {
  const d = new Date()
  d.setDate(d.getDate() - n)
  return d.toISOString()
}

// ── Tenant ────────────────────────────────────────────────────────────
export const demoTenant = {
  tenantId: DEMO_TENANT_ID,
  name:     DEMO_TENANT_NAME,
  domain:   DEMO_TENANT_DOMAIN,
  isActive: true,
  lastScanAt: dataset.tenant?.LastScanAt ?? NOW(),
}
export const demoTenants = [demoTenant]

// ── Compliance Dashboard (drives Baseline Drift pillar) ───────────────
// The Baseline pillar reads passedChecks/totalChecks from this payload.
// Derive from the live metric snapshot for BaselineCompliance.
function latestMetric(type: string) {
  return [...dataset.metricSnapshots]
    .filter(r => r.MetricType === type)
    .sort((a, b) => (b.SnapshotDate ?? '').localeCompare(a.SnapshotDate ?? ''))[0]
}

const baselineMetric    = latestMetric('BaselineCompliance')
const mfaMetric         = latestMetric('MfaAdoption')
const secureScoreMetric = latestMetric('SecureScore')

// "Context" looks like "8/11 resources" — split into passed / total.
function splitContext(ctx?: string | null): { passed: number; total: number } {
  if (!ctx) return { passed: 0, total: 0 }
  const m = /(\d+)\s*\/\s*(\d+)/.exec(ctx)
  if (!m) return { passed: 0, total: 0 }
  return { passed: parseInt(m[1], 10), total: parseInt(m[2], 10) }
}

const baselineSplit = splitContext(baselineMetric?.Context)

// Drift severity histogram — production maps Severity int → string.
// 1=Low, 2=Medium, 3=High, 4=Critical in the dev data.
function severityCounts() {
  let critical = 0, high = 0, medium = 0, low = 0
  for (const d of dataset.drifts) {
    const s = (d as any).Severity
    if (s === 4 || s === 'Critical') critical++
    else if (s === 3 || s === 'High') high++
    else if (s === 2 || s === 'Medium') medium++
    else if (s === 1 || s === 'Low') low++
  }
  return { Critical: critical, High: high, Medium: medium, Low: low }
}

export const demoComplianceDashboard = () => ({
  TenantId: DEMO_TENANT_ID,
  LastUpdated: dataset.tenant?.LastScanAt ?? NOW(),
  OverallScore: {
    TotalChecks:   baselineSplit.total || 11,
    PassedChecks:  baselineSplit.passed || 8,
    FailedChecks:  (baselineSplit.total || 11) - (baselineSplit.passed || 8),
    Level: 'Fair',
    IssuesBySeverity: severityCounts(),
  },
  Metrics: {
    ActiveAlerts: dataset.drifts.filter((d: any) => d.Status === 0 || d.Status === 'Open' || d.Status === 1).length,
    PendingProcessing: 0,
  },
})

// ── Recent activity (Command Center bottom rail) ──────────────────────
// Derived from the drift audit log + latest scan metadata.
export const demoRecentActivity = () => {
  const audit = [...dataset.driftAudit]
    .sort((a: any, b: any) => (b.EventTimestamp ?? '').localeCompare(a.EventTimestamp ?? ''))
    .slice(0, 4)
    .map((r: any, i: number) => ({
      id: r.Id || `audit-${i}`,
      type: (r.EventType ?? '').toLowerCase().includes('approve') ? 'drift_remediated' : 'drift_detected',
      title: r.EventType ?? 'Drift updated',
      description: r.Comments ?? `Drift ${(r.DriftId ?? '').slice(0, 8)} ${r.NewStatus ?? 'updated'}`,
      timestamp: r.EventTimestamp ?? NOW(),
      tenantName: DEMO_TENANT_NAME,
      actionBy: r.TriggeredByDisplayName ?? undefined,
    }))
  const latestScan = [...dataset.metricSnapshots]
    .sort((a: any, b: any) => (b.SnapshotDate ?? '').localeCompare(a.SnapshotDate ?? ''))[0]
  return [
    {
      id: 'scan-latest',
      type: 'baseline_refresh',
      title: 'Baseline scan completed',
      description: `${dataset.drifts.length} drifts open across ${dataset.baselines.length} configurations.`,
      timestamp: latestScan?.SnapshotDate ?? NOW(),
      tenantName: DEMO_TENANT_NAME,
    },
    ...audit,
  ]
}

// ── Drift trends chart ────────────────────────────────────────────────
// 14 day window, severity counts seeded from the current histogram.
export const demoDriftTrends = () => {
  const counts = severityCounts()
  const arr: Array<{ date: string; critical: number; high: number; medium: number; low: number; info: number }> = []
  for (let i = 13; i >= 0; i--) {
    const d = new Date()
    d.setDate(d.getDate() - i)
    arr.push({
      date: d.toISOString().slice(0, 10),
      critical: Math.max(0, counts.Critical + Math.round(Math.sin(i * 0.5) * 1)),
      high:     Math.max(0, counts.High     + Math.round(Math.cos(i * 0.7) * 1)),
      medium:   Math.max(0, counts.Medium   + Math.round(Math.sin(i * 0.4) * 2)),
      low:      Math.max(0, counts.Low      + Math.round(Math.cos(i * 0.6) * 1)),
      info:     0,
    })
  }
  return arr
}

function jitter(base: number, delta: number, min = 0): number {
  const j = Math.round((Math.random() - 0.5) * 2 * delta)
  return Math.max(min, base + j)
}

// ── Dashboard stats (alternate consumer path) ─────────────────────────
export const demoDashboardStats = () => {
  const sev = severityCounts()
  const baselinePct = baselineMetric ? Math.round(baselineMetric.Value) : 73
  return {
    totalTenants: 1,
    activeDrifts: jitter(dataset.drifts.length, 3),
    pendingProcessing: 0,
    criticalIssues: sev.Critical,
    issuesBySeverity: { critical: sev.Critical, high: jitter(sev.High, 1), medium: jitter(sev.Medium, 2), low: jitter(sev.Low, 1) },
    complianceScore: {
      percentage: jitter(baselinePct, 1, 50),
      level: 'Fair',
      totalChecks: baselineSplit.total || 11,
      passedChecks: baselineSplit.passed || 8,
      failedChecks: (baselineSplit.total || 11) - (baselineSplit.passed || 8),
      lastUpdated: NOW(),
    },
    recentActivity: demoRecentActivity(),
    driftTrends: demoDriftTrends(),
    topRisks: [
      { component: 'Best Practice', riskScore: 78, affectedTenants: 1,
        description: `${dataset.violations.filter((v: any) => v.Status === 'Open' && v.Criticality === 'SHALL').length} open SHALL violations across starred frameworks` },
      { component: 'Identity', riskScore: 71, affectedTenants: 1,
        description: `${dataset.userInsights?.MfaDisabledUsers ?? 0} members without MFA, ${dataset.userInsights?.InactiveUsersWithLicenses ?? 0} inactive licensed accounts` },
      { component: 'Baseline Drift', riskScore: 64, affectedTenants: 1,
        description: `${sev.Critical} critical baseline drifts open` },
    ],
  }
}

// ── Recent scan jobs ──────────────────────────────────────────────────
export const demoRecentScanJobs = () => [
  { jobId: 'j-baseline-latest', tenantId: DEMO_TENANT_ID, tenantName: DEMO_TENANT_NAME, scanType: 'Baseline',
    status: 'completed', startedAt: daysAgo(0.04), completedAt: daysAgo(0.03),
    totalControls: dataset.baselines.length, passedControls: Math.max(0, dataset.baselines.length - dataset.drifts.length) },
  { jobId: 'j-bpa-latest', tenantId: DEMO_TENANT_ID, tenantName: DEMO_TENANT_NAME, scanType: 'BestPractice',
    status: 'completed', startedAt: daysAgo(0.05), completedAt: daysAgo(0.045),
    totalControls: dataset.policies.length,
    passedControls: dataset.violations.filter((v: any) => v.Status === 'Pass').length },
  { jobId: 'j-apps-latest', tenantId: DEMO_TENANT_ID, tenantName: DEMO_TENANT_NAME, scanType: 'AppPosture',
    status: 'completed', startedAt: daysAgo(0.06), completedAt: daysAgo(0.055),
    totalControls: dataset.oauthApps.length, passedControls: Math.max(0, dataset.oauthApps.length - 30) },
  { jobId: 'j-userinsights-latest', tenantId: DEMO_TENANT_ID, tenantName: DEMO_TENANT_NAME, scanType: 'UserInsightsRefresh',
    status: 'completed', startedAt: daysAgo(0.15), completedAt: daysAgo(0.14) },
]

// ── Microsoft Secure Score ────────────────────────────────────────────
export const demoSecureScoreSummary = () => {
  const score = dataset.secureScoreData?.Score ?? dataset.secureScoreData?.score
  return {
    score: {
      tenantId: DEMO_TENANT_ID,
      currentScore: score?.CurrentScore ?? score?.currentScore ?? 184,
      maxScore: score?.MaxScore ?? score?.maxScore ?? 354,
      percentage: score?.Percentage ?? score?.percentage ?? 52,
      createdDateTime: score?.CreatedDateTime ?? score?.createdDateTime ?? NOW(),
      enabledServices: score?.EnabledServices ?? score?.enabledServices ?? [],
      activeUserCount: score?.ActiveUserCount ?? score?.activeUserCount ?? 0,
      licensedUserCount: score?.LicensedUserCount ?? score?.licensedUserCount ?? 0,
      controlScores: score?.ControlScores ?? score?.controlScores ?? [],
    },
    recommendations: (dataset.secureScoreData?.Recommendations ?? []).slice(0, 25).map((r: any) => camelDeep(r)),
    totalRecommendations: (dataset.secureScoreData?.Recommendations ?? []).length,
    implementedCount: 0,
    notImplementedCount: 0,
    partialCount: 0,
    categoryBreakdown: {},
    fetchedAt: dataset.tenant?.LastScanAt ?? NOW(),
    fromCache: false,
  }
}

// ── User Analytics ────────────────────────────────────────────────────
export const demoUserInsights = () => {
  const u = dataset.userInsights ?? {}
  const c = camel(u)
  const enabledMembers = u.MemberUsers ?? 0
  const mfaMembers = u.MfaEnabledUsers ?? 0
  const mfaMemberAdoptionPercentage = enabledMembers > 0
    ? Math.round((mfaMembers / enabledMembers) * 1000) / 10
    : 0
  return {
    ...c,
    enabledMemberUsers: enabledMembers,
    mfaEnabledMembers: mfaMembers,
    mfaMemberAdoptionPercentage,
    authenticatorAppMembers: u.AuthenticatorAppUsers ?? 0,
    authenticatorMemberAdoptionPercentage: enabledMembers > 0
      ? Math.round(((u.AuthenticatorAppUsers ?? 0) / enabledMembers) * 1000) / 10
      : 0,
    activeMemberUsers: enabledMembers,
    activeGuestUsers: u.GuestUsers ?? 0,
    inactiveMemberUsers: 0,
    inactiveGuestUsers: 0,
    needsAttentionUsers: u.MfaDisabledUsers ?? 0,
    noMfaEnabledUsers: u.MfaDisabledUsers ?? 0,
    staleUsers: u.InactiveUsersWithLicenses ?? 0,
    licenseWasteCount: u.TotalLicenseWaste ?? 0,
    hasData: true,
    phishResistantMfaPercentage: enabledMembers > 0
      ? Math.round(((u.PhishResistantMfaUsers ?? 0) / enabledMembers) * 1000) / 10
      : 0,
    inactivityThresholdDays: 90,
  }
}

// ── SCuBA Deep ────────────────────────────────────────────────────────
export const demoScubaSummary = () => {
  const baselines = dataset.scubaProducts.map((p: any) => ({
    name: p.Product ?? p.ProductName ?? 'Unknown',
    score: Math.round(p.OverallScore ?? 0),
    totalChecks: p.TotalControls ?? 0,
    passed: p.PassedControls ?? 0,
    failed: p.FailedControls ?? 0,
    warn:   p.WarnControls ?? 0,
  }))
  const totalControls = baselines.reduce((s, b) => s + b.totalChecks, 0)
  const passCount = baselines.reduce((s, b) => s + b.passed, 0)
  const failCount = baselines.reduce((s, b) => s + b.failed, 0)
  const warnCount = baselines.reduce((s, b) => s + b.warn, 0)
  return {
    tenantId: DEMO_TENANT_ID,
    tenantName: DEMO_TENANT_NAME,
    totalControls,
    passCount,
    failCount,
    warnCount,
    manualCount: Math.max(0, totalControls - passCount - failCount - warnCount),
    baselines,
    overallScore: passCount + failCount > 0 ? Math.round((passCount / (passCount + failCount)) * 100) : 0,
    lastScanAt: dataset.scubaReport?.GeneratedAt ?? dataset.scubaReport?.CreatedAt ?? daysAgo(2),
  }
}

// ── Compliance Scores (drives Best Practice pillar) ───────────────────
export const demoComplianceScores = () => {
  const fbyId: Record<string, any> = {}
  for (const f of dataset.frameworks) fbyId[f.Id] = f

  const passRate = (frameworkCode: string) => {
    let pass = 0, total = 0
    for (const v of dataset.violations) {
      const det = (v as any).Details ?? ''
      if (typeof det === 'string' && det.includes(frameworkCode)) {
        total++
        if ((v as any).Status === 'Pass') pass++
      }
    }
    if (total === 0) return { score: 0, passed: 0, total: 0 }
    return { score: Math.round((pass / total) * 100), passed: pass, total }
  }

  const standardScores = dataset.tenantStandards.map((s: any) => {
    const f = fbyId[s.StandardId]
    const code = f?.Code ?? ''
    const stats = passRate(code)
    return {
      StandardId: s.StandardId,
      StandardCode: code,
      StandardName: f?.Name ?? '',
      RawScore: stats.score,
      AdjustedScore: stats.score,
      WeightedScore: stats.score,
      Weight: s.Weight ?? 100,
      PassedControls: stats.passed,
      FailedControls: Math.max(0, stats.total - stats.passed),
      WaivedControls: 0,
      TotalControls: stats.total || f?.TotalControls || 0,
      LastEvaluatedAt: dataset.tenant?.LastScanAt ?? NOW(),
      IsEnabled: s.IsEnabled ?? true,
      IncludeInGuardianScore: s.IncludeInGuardianScore ?? true,
    }
  })

  return {
    TenantId: DEMO_TENANT_ID,
    OverallScore: Math.round(
      standardScores
        .filter(s => s.IncludeInGuardianScore)
        .reduce((sum, s) => sum + s.WeightedScore, 0) /
      Math.max(1, standardScores.filter(s => s.IncludeInGuardianScore).length),
    ),
    TotalPassedControls: standardScores.reduce((s, x) => s + x.PassedControls, 0),
    TotalFailedControls: standardScores.reduce((s, x) => s + x.FailedControls, 0),
    LastEvaluatedAt: dataset.tenant?.LastScanAt ?? NOW(),
    StandardScores: standardScores,
    scoreHistory: dataset.scoreHistory
      .filter((r: any) => r.SnapshotDate)
      .slice(-12)
      .map((r: any) => ({
        date: typeof r.SnapshotDate === 'string' ? r.SnapshotDate.slice(0, 10) : '',
        guardianScore: r.Score,
        secureScore: secureScoreMetric?.Value ?? null,
        bpaScore: r.Score,
        mfaAdoption: mfaMetric?.Value ?? null,
        baselineCompliance: baselineMetric?.Value ?? null,
      })),
  }
}

// ── Executive Report ──────────────────────────────────────────────────
export const demoExecutiveReport = () => {
  const baseline = Math.round(baselineMetric?.Value ?? 73)
  const mfa = Math.round(mfaMetric?.Value ?? 31)
  const secure = Math.round(secureScoreMetric?.Value ?? 52)
  const bpa = (() => {
    const scores = demoComplianceScores().StandardScores.filter(s => s.IncludeInGuardianScore)
    if (scores.length === 0) return 0
    return Math.round(scores.reduce((s, x) => s + x.WeightedScore, 0) / scores.length)
  })()
  const overall = Math.round((baseline * 0.2) + (bpa * 0.3) + (mfa * 0.2) + (secure * 0.3))
  const start  = Math.max(0, overall - 4)

  const today = new Date()
  const trajectory = Array.from({ length: 30 }, (_, i) => {
    const d = new Date(today); d.setDate(today.getDate() - (29 - i))
    return {
      Date: d.toISOString().slice(0, 10),
      GuardianScore: Math.min(100, Math.max(0, start + Math.round((i / 29) * (overall - start)) + Math.round(Math.sin(i * 0.5) * 1.5))),
      SecureScore: secure + Math.round(Math.cos(i * 0.5) * 1),
      BpaScore: bpa + Math.round(Math.sin(i * 0.4) * 1),
      MfaAdoption: mfa + Math.round(Math.cos(i * 0.5) * 1),
      BaselineCompliance: baseline + Math.round(Math.sin(i * 0.4) * 1),
    }
  })

  const openShall  = dataset.violations.filter((v: any) => v.Status === 'Open' && v.Criticality === 'SHALL').length
  const openShould = dataset.violations.filter((v: any) => v.Status === 'Open' && v.Criticality === 'SHOULD').length
  const totalMembers = dataset.userInsights?.MemberUsers ?? 0
  const mfaDisabled = dataset.userInsights?.MfaDisabledUsers ?? 0
  const inactiveLicensed = dataset.userInsights?.InactiveUsersWithLicenses ?? 0

  return {
    TenantId: DEMO_TENANT_ID,
    TenantName: DEMO_TENANT_NAME,
    GeneratedAt: NOW(),
    ReportPeriodDays: 30,
    CurrentGuardianScore: overall,
    PeriodStartGuardianScore: start,
    LetterGrade: overall >= 85 ? 'A' : overall >= 75 ? 'B' : overall >= 65 ? 'C' : overall >= 55 ? 'D' : 'F',
    Rating: overall >= 80 ? 'Strong' : overall >= 65 ? 'Mixed' : overall >= 50 ? 'Soft' : 'Fragile',
    BaselineCompliance: { Label: 'Baseline Compliance', Current: baseline, PeriodStart: baseline - 1, Change: 1, Direction: 'up', Weight: 20 },
    BpaScore:           { Label: 'Best Practice',       Current: bpa,      PeriodStart: bpa - 3,     Change: 3, Direction: 'up', Weight: 30 },
    MfaAdoption:        { Label: 'MFA Adoption',        Current: mfa,      PeriodStart: mfa - 2,     Change: 2, Direction: 'up', Weight: 20 },
    SecureScore:        { Label: 'M365 Secure Score',   Current: secure,   PeriodStart: secure - 1,  Change: 1, Direction: 'up', Weight: 30 },
    FrameworkScores: demoComplianceScores().StandardScores.map(s => ({
      Code: s.StandardCode,
      Name: s.StandardName,
      Score: s.WeightedScore,
      PassedControls: s.PassedControls,
      TotalControls: s.TotalControls,
      PeriodStartScore: Math.max(0, s.WeightedScore - 4),
      Change: 4.0,
      Direction: 'up',
      IncludeInGuardianScore: s.IncludeInGuardianScore,
    })),
    Metrics: {
      ViolationsResolved: 0,
      DriftItemsRemediated: dataset.driftAudit.filter((r: any) => (r.NewStatus ?? '').toLowerCase().includes('approve') || (r.EventType ?? '').toLowerCase().includes('approve')).length,
      WaiversGranted: dataset.waivers.length,
      OpenCriticalViolations: openShall,
      OpenHighViolations: openShould,
      TotalDriftDetected: dataset.drifts.length,
    },
    GuardianScoreTrend: trajectory,
    ExecutiveNarrative: [
      `Guardian Score is ${overall}/100 over the trailing 30-day window, up from ${start} a month ago. ` +
      `Best Practice climbed 3 points to ${bpa}; MFA adoption sits at ${mfa}% and Microsoft Secure Score at ${secure}%. ` +
      `Baseline configuration coverage is at ${baseline}%. The composite moved in your favor because the heaviest-weighted pillars stopped sliding.`,

      `The strongest current signal is best-practice coverage. There are ${openShall} open SHALL violations and ${openShould} open SHOULD findings ` +
      `across starred frameworks — these are not incidents, but unresolved configuration gaps that affect compliance posture and increase the surface for incident progression.`,

      `Identity is the next lever. ${mfaDisabled} of ${totalMembers} members are not yet enrolled in MFA, ` +
      `and ${inactiveLicensed} licensed accounts have been inactive long enough to warrant review. ` +
      `Closing the MFA gap is the fastest measurable move on the score.`,

      `Next period: prioritize the open SHALL list, push MFA enrollment to the remaining members, and triage the ` +
      `${dataset.drifts.length} active baseline drifts. The compliance trend shows momentum — sustaining it for one more sprint should bring the composite into the next band.`,
    ].join('\n\n'),
  }
}

// ── OAuth Apps / App Posture ──────────────────────────────────────────
function riskBucket(level: any): 'high' | 'medium' | 'low' {
  if (level === 'High' || level === 3) return 'high'
  if (level === 'Medium' || level === 2) return 'medium'
  return 'low'
}

export const demoOAuthAppsSummary = () => {
  const total = dataset.oauthApps.length
  const high = dataset.oauthApps.filter((a: any) => riskBucket(a.RiskLevel) === 'high').length
  const med  = dataset.oauthApps.filter((a: any) => riskBucket(a.RiskLevel) === 'medium').length
  const low  = dataset.oauthApps.filter((a: any) => riskBucket(a.RiskLevel) === 'low').length
  const ack  = dataset.oauthApps.filter((a: any) => a.IsAcknowledged).length
  return {
    TenantId: DEMO_TENANT_ID,
    TotalApps: total,
    HighRisk: high,
    MediumRisk: med,
    LowRisk: low,
    UnclassifiedApps: Math.max(0, total - high - med - low),
    AcknowledgedApps: ack,
    FlaggedForAttention: high + med,
    LastSyncedAt: dataset.tenant?.LastScanAt ?? NOW(),
  }
}

export const demoOAuthApps = () => ({
  apps: dataset.oauthApps.slice(0, 50).map((a: any) => ({
    id: a.Id,
    displayName: a.DisplayName ?? a.AppDisplayName ?? a.Name ?? 'Unnamed app',
    publisherName: a.PublisherName ?? a.PublisherDisplayName ?? 'Unknown publisher',
    riskLevel: riskBucket(a.RiskLevel),
    isAcknowledged: !!a.IsAcknowledged,
    permissionCount: dataset.oauthPermissions.filter((p: any) => p.OAuthAppId === a.Id).length,
    addedAt: a.CreatedAt ?? a.FirstConsentAt ?? daysAgo(180),
  })),
  totalCount: dataset.oauthApps.length,
  pageNumber: 1,
  pageSize: 50,
})

function scopeRiskWeight(scope: string): number {
  const s = scope.toLowerCase()
  if (s.includes('mail.read') || s.includes('mail.send') || s.includes('files.read.all') || s.includes('sites.readwrite.all')) return 9
  if (s.includes('group.readwrite') || s.includes('directory.readwrite')) return 8
  if (s.includes('calendars.read') || s.includes('files.read')) return 5
  if (s.includes('user.read') && !s.includes('all')) return 2
  return 4
}

export const demoPermissionHeatmap = () => {
  const tally: Record<string, { consents: number; riskWeight: number }> = {}
  for (const p of dataset.oauthPermissions) {
    const name = (p as any).Permission ?? (p as any).PermissionName ?? (p as any).Scope ?? ''
    if (!name) continue
    tally[name] = tally[name] ?? { consents: 0, riskWeight: scopeRiskWeight(name) }
    tally[name].consents++
  }
  const permissions = Object.entries(tally)
    .map(([name, v]) => ({ name, ...v }))
    .sort((a, b) => b.consents - a.consents)
    .slice(0, 12)
  return { generatedAt: NOW(), permissions }
}

export const demoRecentConsents = () => {
  return [...dataset.oauthConsents]
    .sort((a: any, b: any) => (b.ConsentedAt ?? b.CreatedAt ?? '').localeCompare(a.ConsentedAt ?? a.CreatedAt ?? ''))
    .slice(0, 10)
    .map((c: any) => {
      const app = dataset.oauthApps.find((a: any) => a.Id === c.OAuthAppId)
      return {
        id: c.Id,
        appName: app?.DisplayName ?? app?.AppDisplayName ?? 'Unknown app',
        riskLevel: riskBucket(app?.RiskLevel),
        scope: c.Scope ?? c.ConsentedScope ?? '',
        consentedAt: c.ConsentedAt ?? c.CreatedAt ?? daysAgo(7),
      }
    })
}

export const demoSecureScoreRecommendations = () => {
  const recs = dataset.secureScoreData?.Recommendations ?? []
  return recs.slice(0, 25).map((r: any) => ({
    id: r.Id ?? r.id,
    controlName: r.Title ?? r.title ?? r.Id,
    category: r.ControlCategory ?? r.controlCategory ?? 'General',
    impact: r.UserImpact ?? r.userImpact ?? 'medium',
    scoreImpact: r.MaxScore ?? r.maxScore ?? 0,
  }))
}

export const demoTenantStandards = () =>
  dataset.tenantStandards.map((s: any) => {
    const f = dataset.frameworks.find((x: any) => x.Id === s.StandardId)
    return {
      Id: s.Id,
      TenantId: DEMO_TENANT_ID,
      Code: f?.Code ?? '',
      Name: f?.Name ?? '',
      AssignedAt: s.AssignedAt,
      AssignedBy: s.AssignedBy,
      Status: s.IsEnabled ? 'Active' : 'Inactive',
      IncludeInGuardianScore: !!s.IncludeInGuardianScore,
    }
  })

// ── Phish-resistant MFA breakdown (User Analytics) ────────────────────
export const demoPhishMfa = () => {
  const u = dataset.userInsights ?? {}
  return {
    TenantId: DEMO_TENANT_ID,
    PhishResistantUsers: u.PhishResistantMfaUsers ?? 0,
    StandardMfaUsers:    Math.max(0, (u.MfaEnabledUsers ?? 0) - (u.PhishResistantMfaUsers ?? 0)),
    NoMfaUsers:          u.MfaDisabledUsers ?? 0,
    TotalLicensedUsers:  u.TotalLicensedUsers ?? 0,
    AdoptionPercentage:  u.MfaAdoptionPercentage ?? 0,
    LastUpdated: NOW(),
  }
}

// ── License utilization ───────────────────────────────────────────────
export const demoLicenseSkus = () =>
  dataset.licenseSkus.map((s: any) => ({
    skuId: s.SkuId ?? s.Id,
    skuPartNumber: s.SkuPartNumber ?? s.SkuId ?? 'UNKNOWN',
    displayName: s.DisplayName ?? s.SkuPartNumber ?? 'License',
    totalLicenses: s.TotalLicenses ?? 0,
    usedLicenses: s.UsedLicenses ?? s.AssignedLicenses ?? 0,
    availableLicenses: s.AvailableLicenses ?? Math.max(0, (s.TotalLicenses ?? 0) - (s.UsedLicenses ?? 0)),
    utilizationPercentage: s.UtilizationPercentage ?? (s.TotalLicenses > 0 ? Math.round(((s.UsedLicenses ?? 0) / s.TotalLicenses) * 1000) / 10 : 0),
    monthlyPricePerLicense: s.MonthlyPricePerLicense ?? null,
    estimatedMonthlyWasteUsd: s.EstimatedMonthlyWasteUsd ?? null,
  }))

// ── Activity feed (Command Center) ────────────────────────────────────
export const demoActivityFeed = () => demoRecentActivity().map((a, i) => ({
  ...a,
  pillar: i % 3 === 0 ? 'compliance' : i % 3 === 1 ? 'identity' : 'bpa',
  source: 'Guardian',
}))

// ── Priority actions queue (Command Center top six) ───────────────────
function detSinceMeta(iso?: string): string {
  if (!iso) return 'recently detected'
  const ms = Date.now() - new Date(iso).getTime()
  const days = Math.max(1, Math.round(ms / (1000 * 60 * 60 * 24)))
  return `${days}d`
}

export const demoPriorityActions = () => {
  const openShalls = dataset.violations
    .filter((v: any) => v.Status === 'Open' && v.Criticality === 'SHALL')
    .slice(0, 6)
  const productGuess = (msg: string): string => {
    const m = msg.toLowerCase()
    if (m.includes('exchange')) return 'Exchange'
    if (m.includes('sharepoint')) return 'SharePoint'
    if (m.includes('teams')) return 'Teams'
    if (m.includes('intune')) return 'Intune'
    return 'EntraID'
  }
  return openShalls.map((v: any, i: number) => {
    const policy = dataset.policies.find((p: any) => p.Id === v.PolicyId || p.Code === v.PolicyCode)
    let det: any = v.Details
    if (typeof det === 'string') { try { det = JSON.parse(det) } catch { det = null } }
    const title = det?.Title || policy?.Title || policy?.Name || policy?.Description || v.Message?.split('.')[0] || 'Address SHALL violation'
    return {
      id: v.Id ?? `pa-${i}`,
      rank: i + 1,
      title,
      pillar: 'bpa' as const,
      severity: (i < 2 ? 'crit' : i < 4 ? 'high' : 'med') as 'crit' | 'high' | 'med',
      meta: `${productGuess(title + ' ' + (v.Message ?? ''))} / ${v.PolicyCode ?? 'POL'} / ${detSinceMeta(v.FirstDetectedAt)}`,
      href: '/security?tab=best-practices',
      learnMoreUrl: null,
      impact: 90 - i * 8,
      scope: 1,
      frameworksAffected: det?.Frameworks?.length ?? 1,
      guardianScoreDelta: Math.round((1.0 - i * 0.1) * 10) / 10,
    }
  })
}

// ── Baseline drifts (Baseline Compliance "Top items" + queue) ─────────
const SEVERITY_INT_TO_STRING: Record<number, string> = { 1: 'Low', 2: 'Medium', 3: 'High', 4: 'Critical' }
const STATUS_INT_TO_STRING: Record<number, string> = { 0: 'New', 1: 'New', 2: 'Reviewed', 3: 'Approved', 4: 'Rejected', 5: 'Approved' }

export const demoBaselineDrifts = () => dataset.drifts.slice(0, 80).map((d: any) => ({
  id: d.Id,
  tenantId: DEMO_TENANT_ID,
  component: d.Component ?? d.ComponentType,
  componentName: d.ComponentName ?? '',
  setting: d.Setting ?? d.PropertyName ?? '',
  oldValue: d.OldValue ?? '',
  newValue: d.NewValue ?? '',
  severity: typeof d.Severity === 'number'
    ? SEVERITY_INT_TO_STRING[d.Severity] ?? 'Medium'
    : (d.Severity ?? 'Medium'),
  status: typeof d.Status === 'number'
    ? STATUS_INT_TO_STRING[d.Status] ?? 'New'
    : (d.Status ?? 'New'),
  detectedAt: d.DetectedAt ?? d.CreatedAt ?? NOW(),
  humanReadableDescription: d.HumanReadableDescription ?? '',
  riskScore: typeof d.RiskScore === 'number' && d.RiskScore <= 1 ? d.RiskScore : (d.RiskScore ?? 50) / 100,
  complianceFrameworkImpact: Array.isArray(d.FrameworksAffected) ? d.FrameworksAffected : [],
}))

// ── Current user ──────────────────────────────────────────────────────
export const demoCurrentUser = {
  isGlobalAdmin: false,
  email: 'jane.doe@contoso.onmicrosoft.com',
  displayName: 'Jane Doe',
  role: 'TenantViewer',
}
